import type { ArticleSummary, Image } from '@publiqa/shared'
import { formatDate } from '../lib/richtext'
import { SmartLink } from './context'

/** Image, ou aplat dégradé aux couleurs du thème quand il n'y en a pas encore. */
export function Picture({
  image,
  className = '',
  label,
  alt,
  eager = false,
}: {
  image: Image | null
  className?: string
  label?: string
  /** Texte alternatif si l'image n'en a pas (titre de l'article, nom…). */
  alt?: string
  eager?: boolean
}) {
  if (!image) {
    return (
      <div className={`ph ${className}`} role="img" aria-label={label ?? ''}>
        {label && <span>{label}</span>}
      </div>
    )
  }
  return (
    <img
      className={className}
      src={image.url}
      alt={image.alt || alt || label || ''}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
    />
  )
}

export function Kicker({ article }: { article: ArticleSummary }) {
  const label = article.category?.name ?? article.kicker
  if (!label) return null
  return (
    <span
      className="kicker"
      style={article.category ? { color: article.category.color } : undefined}
    >
      {label}
    </span>
  )
}

export function ArticleCard({
  article,
  variant = 'card',
  badge = false,
}: {
  article: ArticleSummary
  variant?: 'card' | 'lead' | 'row' | 'compact' | 'text' | 'overlay' | 'side'
  /** Affiche « À la une » sur les articles mis en avant. */
  badge?: boolean
}) {
  const href = `/article/${article.slug}`
  const date = (
    <time className="acard-date" dateTime={article.publishedAt ?? undefined}>
      {formatDate(article.publishedAt)}
    </time>
  )
  const pill = article.category?.name ?? article.kicker
  // Tuile photo et vignette latérale : pastille de rubrique SUR l'image.
  if (variant === 'overlay' || variant === 'side') {
    return (
      <article className={`acard acard-${variant}`}>
        <div className="acard-media">
          <Picture image={article.cover} alt={article.title} eager={variant === 'overlay'} />
          {pill && <span className="acard-pill">{pill}</span>}
        </div>
        <div className="acard-body">
          {badge && article.featured && <span className="badge-une">À la une</span>}
          {date}
          <h3 className="acard-title">
            <SmartLink href={href}>{article.title}</SmartLink>
          </h3>
          {article.excerpt && <p className="acard-excerpt">{article.excerpt}</p>}
        </div>
      </article>
    )
  }
  return (
    <article className={`acard acard-${variant}`}>
      {variant !== 'compact' && variant !== 'text' && (
        <SmartLink href={href} className="acard-media" tabIndex={-1} aria-hidden="true">
          <Picture image={article.cover} alt={article.title} eager={variant === 'lead'} />
        </SmartLink>
      )}
      <div className="acard-body">
        {badge && article.featured && <span className="badge-une">À la une</span>}
        <Kicker article={article} />
        <h3 className="acard-title">
          <SmartLink href={href}>{article.title}</SmartLink>
        </h3>
        {(variant === 'lead' || variant === 'card' || variant === 'row') && article.excerpt && (
          <p className="acard-excerpt">{article.excerpt}</p>
        )}
        <p className="acard-meta">
          {article.status === 'draft' && <span className="badge-draft">Brouillon</span>}
          {article.author && variant !== 'compact' && <span>{article.author}</span>}
          <time dateTime={article.publishedAt ?? undefined}>{formatDate(article.publishedAt)}</time>
        </p>
      </div>
    </article>
  )
}

export function Loading() {
  return <div className="pub-loading" aria-busy="true" />
}

export function NotFound({ message = 'Cette page n’existe pas ou plus.' }: { message?: string }) {
  return (
    <section className="pub-notfound container">
      <p className="nf-code">404</p>
      <h1>Page introuvable</h1>
      <p>{message}</p>
      <SmartLink href="/" className="btn btn-primary">
        Retour à l’accueil
      </SmartLink>
    </section>
  )
}

export function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="mg-icon" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}

export function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" className="mg-icon" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  )
}
